import { isDevelopment, isStaging } from '@/lib/appConstants';
import { getUniqueId } from '@/lib/commonHelpers';
// From the leaf module, NOT commonHelpers — see lsHelpers.ts (ChromeOS boot TDZ).
import { getFromLS, setToLS } from '@/lib/lsHelpers';
import { message } from '@/lib/notify/apNotify';
import * as Sentry from '@sentry/react';
import {
  AppMethodParams,
  checkNativeMethodSupported,
  DownloadHelper,
  getAppType,
  getAppVersion,
  isNewAppVersion,
} from './OfflineAppHelper';

window.appType = 'web';

/**
 * Wire contract for the Mac native `nativeBatchMutation` bridge handler
 * (APL-11641). Each entry is a single Firestore document write: `path` is the
 * full `collection/doc/subcollection/doc` path, `data` is the document payload,
 * `actionType` the write mode. The array is JSON-stringified and handed across
 * the bridge fire-and-forget; native applies its own server `updatedAt`.
 */
export type NativeBatchOperation = {
  path: string;
  data: any;
  actionType: 'set' | 'update' | 'delete';
};

/**
 * Minimal local shape of a batch value (structural subset of the data
 * provider's `RealtimeDbCreateManyValue`). Defined locally on purpose — a
 * type import from `@/providers/dataProvider/firestoreDataProvider` pulls that
 * module into this file's import graph and reorders evaluation, which trips a
 * `getAppType` TDZ via the OfflineAppHelper ⇄ OfflineAppService cycle.
 */
type NativeBatchMutationValue = {
  resource: string;
  resourcePath?: string;
  ids?: string[];
  data: any;
  actionType?: 'set' | 'update' | 'delete';
};

/**
 * APL-11850 — iPad settle window after lockdown is switched ON.
 *
 * The iPad app reconfigures its network stack as part of enabling security, so
 * for a few seconds after native acks `changeSecurity` the connection is still
 * stabilising. Resolving the bridge promise immediately hands that half-settled
 * network to the very next step (navigate → Firestore sign-in + boot reads),
 * which is where the init-timing failures cluster. Hold the resolve so callers
 * start against a settled connection. iPad + enable only — the disable path and
 * the Mac app are untouched.
 *
 * 10s, widened from the first cut's 5s: the settle is slower than it looked on
 * the bench, and the cost of overshooting is a few extra seconds on the
 * lockdown-enable screen, while undershooting hands boot the half-settled
 * network this window exists to avoid.
 */
const IOS_SECURITY_ENABLE_SETTLE_MS = 10_000;

// Helper function to generate UUID
const generateUUID = (): string => getUniqueId();

// Helper function to dispatch events
const dispatchEvent = (eventName: string, value: any) => {
  const event = new CustomEvent(eventName, { detail: value });
  window.dispatchEvent(event);
};

// Initialize the service method
export const initializeLockdownAppServices = () => {
  const finalServiceObject: { [key: string]: any } = {};

  const appType: any = getAppType(); //window.appType || 'web';
  const isMac = appType === 'mac';
  const isWin = appType === 'win';
  const isIos = appType === 'ios';

  let win: DownloadHelper | undefined, mac: { postMessage: (args: any) => void } | undefined;

  // Set platform-specific helpers
  if (isWin) win = window.downloadHelper;
  if (isMac && window.webkit?.messageHandlers)
    mac = window.webkit.messageHandlers.macDownloadHelper;
  if (isIos)
    mac = window.webkit?.messageHandlers && window.webkit.messageHandlers.iosDownloadHelper;

  // Initialize state and intervals
  const promisesData: Record<string, any> = {};
  const newInterval: { [key: string]: NodeJS.Timeout } = {};
  interface NativeData {
    guid: string;
    key: string;
    value: string;
    callbackType: string;
  }
  const appExecutedMethodData: { [key: string]: NativeData } = {};

  // Handle focus lost event
  window.executeSetFocusLost = (value) => {
    dispatchEvent('AssessPrepFocusLost', value);
  };

  // Handle security interrupted event
  window.executeSetSecurityWasInterrupted = (value) => {
    dispatchEvent('SetAACSecurityInterruptedLog', value);
  };

  // Handle security resume event
  window.executeSetLogOnSecurityResume = (value) => {
    dispatchEvent('SetSecurityResumeLog', value);
  };

  // Handle session end event
  window.executeOnBeforeEndSession = (value) => {
    dispatchEvent('onBeforeEndSession', value);
  };

  // Handle app quit event
  window.executeSetLogOnQuit = (value) => {
    const currentSystemAppVersion = getAppVersion(appType);
    if (appType === 'mac' && !isNewAppVersion(currentSystemAppVersion, '12.0.0')) {
      setToLS('securityHasBeenInterrupted', null);
    }
    dispatchEvent('SetNativeQuitLog', value);
  };

  // Handle auto-update status event
  window.executeSetAutoUpdateStatus = (value) => {
    dispatchEvent('SetAutoUpdateStatus', value);
  };

  // Handle promise execution
  window.executePromise = (guid: string, data: string): void => {
    const changeSecurityGuidFromLS = getFromLS('changeSecurityGuid');
    const changeSecurityValueFromLS = getFromLS('changeSecurityValue');

    if (
      changeSecurityGuidFromLS === guid &&
      changeSecurityValueFromLS === 'true' &&
      data === 'false'
    ) {
      dispatchEvent('AACSecurityInterrupted', data);
      setToLS('changeSecurityGuid', null);
      setToLS('changeSecurityValue', null);
    }

    promisesData[guid] = data;
  };

  // Handle app method execution
  let executeAppMethod = ({ key, value }: AppMethodParams): Promise<any> | void => {
    console.log('No execute method found for', appType, key, value);
  };

  // Handle native methods for macOS/iOS
  if (isMac || isIos) {
    executeAppMethod = ({ key, value }) => {
      // These methods are fire-and-forget; the native side never calls back executePromise.
      const noCallbackKeys = ['setSentryUser', 'printLog'];
      const noCallback = noCallbackKeys.includes(key);
      // Map key to corresponding native function
      // Supported native version per method (from the original switch-case,
      // APL-11633). "Supported from" = the app build that first shipped the
      // native handler; calling a method on an older build means no handler is
      // registered and the executePromise channel never resolves. mac/ios
      // minimums differ; web/seb/win have no native bridge here.
      const methodMappings: Record<string, string> = {
        quit: 'quitAssessPrep', // supported from app version >= 9.0.0
        changeSecurity: 'changeSecurity', // supported from app version >= 9.0.0
        getBatteryStatus: 'getBatteryStatus', // supported from app version >= 9.0.0
        checkIfAppSwitched: 'checkIfAppSwitched', // supported from app version >= 9.0.0 (native triggers)
        setAppSwitched: 'setAppSwitched', // supported from app version >= 9.0.0 (sets false)
        startUpdate: 'startUpdate', // supported from app version >= 11.0.0
        closeAllApps: 'closeAllApps', // supported from app version >= 9.0.0
        downloadFile: 'downloadFile', // supported from app version >= 9.0.0
        saveTestData: 'saveTestData', // supported from app version >= 9.0.0
        exportToDesktop: 'exportJSONToFile', // supported from app version >= 9.0.0
        clearClipboard: 'clearClipboard', // supported from app version >= 9.0.0
        openLinkInBrowser: 'openLinkInBrowser', // supported from app version >= 9.0.0
        setCountryName: 'setCountryName', // supported from app version >= 9.0.0
        getCountryName: 'getCountryName', // supported from app version >= 9.0.0
        setAppUrl: 'setAppUrl', // supported from app version >= 9.0.0
        getAppUrl: 'getAppUrl', // supported from app version >= 9.0.1
        setWhitelistedUrls: 'setWhitelistedUrls', // supported from app version >= 9.0.0
        clearCache: 'clearCache', // supported from app version >= 9.0.1 (clears disk+memory cache, NOT IndexedDB)
        clearCacheWithSession: 'clearCacheWithSession', // supported from app version >= 9.0.1 (also cookies+session storage, NOT IndexedDB)
        openNewWindow: 'openNewWindow', // supported from app version >= 10.1.0 (used to open zoom call)
        openChildWindow: 'openChildWindow', // supported from app version >= 10.1.0 (iframe in new window, no minimize)
        openContentInModal: 'openContentInModal', // supported from app version >= 11.0.0 (iframe in modal, with minimize)
        closeAllNewWindow: 'closeAllNewWindow', // supported from app version >= 10.1.0
        closeAllChildWindow: 'closeAllChildWindow', // supported from app version >= 10.1.0
        showQuitAlert: 'showQuitAlert', // supported from app version >= 10.2.0
        getSecurityStatus: 'getSecurityStatus', // supported from app version >= 10.2.0
        allowScreenSharing: 'allowScreenSharing', // supported from app version >= 10.1.0
        setSentryUser: 'setSentryUser', // supported from app version >= 11.0.0
        exitApp: 'exitApp', // iPad (ios) only — supported from app version >= 3.2.0
        printLog: 'printLog', // mac only — supported from app version >= 12.1.0 (NOTE: checkNativeMethodSupported gates printLog at 12.1.1)
        // Mac native enumerates NSWorkspace.runningApplications and posts
        // the result back through the standard executePromise channel as a
        // JSON string. The parsed snapshot is attached to the
        // `test_started_in_app` application-log entry by StartTestButton
        // (`current_value.running_processes`) and fed into the flagged-apps
        // security-report POST by useReportRunningProcesses.
        // Supported on the Mac app ≥ 12.3.0 only — callers must gate via
        // `checkNativeMethodSupported(appType, 'getNativeRunningProcessApps')`
        // so older builds (no native handler) don't time out into Sentry.
        getNativeRunningProcessApps: 'getNativeRunningProcessApps',
        // Mac native reads the student's macOS Text Replacements list.
        // Consumed by `useSystemTextReplacements`.
        // Supported on the Mac app ≥ 12.3.0 only — callers must gate via
        // `checkNativeMethodSupported(appType, 'getSystemTextReplacements')`.
        getSystemTextReplacements: 'getSystemTextReplacements',
        // Mac native signs the supplied bytes with the device's Secure
        // Enclave key (registered during PKCE login) and returns a JSON
        // payload `{ signature, key_id, signed_input }` via the standard
        // executePromise channel. See `src/lib/offline/secureEnclave.ts`
        // for the consumer-side helper.
        // Supported on the Mac app ≥ 12.3.0 only — `isSecureEnclaveSupported()`
        // gates this via `checkNativeMethodSupported`.
        signWithSecureEnclave: 'signWithSecureEnclave',
        // React hands the Firestore custom token to native; native signs into
        // Firebase Auth (signInWithCustomToken) and from then on owns the
        // user_response writes (APL-11641). Acks 'true'/'false' over the standard
        // executePromise channel (NOT fire-and-forget — the caller needs the
        // sign-in result), so it is intentionally kept out of `noCallbackKeys`.
        // Caller-side gating lives in `signInNativeFirestore` (OfflineAppService),
        // which checks `checkNativeMethodSupported(getAppType(), 'signInNativeFirestore')`
        // → Mac app >= 13.0.0 / iOS app >= 6.0.0. Native handler carries the
        // same name (PR #6578 review).
        signInNativeFirestore: 'signInNativeFirestore',
        // Native handler that takes a JSON array of { path, data, actionType }
        // ops and writes them to Firestore directly (APL-11641). Once React has
        // handed off the Firestore custom token via signInNativeFirestore, native
        // owns the user_response batch writes — React builds the same operations
        // it would have committed over the web SDK and hands them across the
        // bridge. Native acks each batch over executePromise with 'ok'
        // (server-confirmed), 'ok:cached' (saved to the offline cache, auto-syncs
        // on reconnect) or 'error:<reason>' (write failed) — see
        // `nativeBatchMutation` below, which retries an 'error:' ack once. NOTE:
        // `nativeBatchMutation` is an external
        // contract — confirm the exact native handler name with the native dev;
        // defaulting to an identity name for now. Caller-side gating lives in
        // `checkNativeMethodSupported(getAppType(), 'nativeBatchMutation')`
        // → Mac app >= 13.0.0 / iOS app >= 6.0.0.
        nativeBatchMutation: 'saveBatchUserResponses',
        // Native reports whether its Firestore write queue has flushed (APL-11641):
        // 'synced' / 'pending'. CALLBACK method (caller needs the ack) → kept out
        // of noCallbackKeys. External contract — confirm handler name + ack tokens
        // with the native dev (B-1); defaulting to the identity name for now.
        // Gated in `waitForNativePendingWritesSynced` (Mac >= 13.0.0 / iOS >= 6.0.0).
        getFirestorePendingWritesStatus: 'getFirestorePendingWritesStatus',
      };

      const mappedFunction = methodMappings[key] || 'nativeLog';

      const newPromiseId = generateUUID();
      const nativeData = {
        guid: newPromiseId,
        key: mappedFunction,
        value: value?.toString() || '',
        callbackType: 'method/data',
      };

      appExecutedMethodData[newPromiseId] = nativeData;
      mac?.postMessage?.(nativeData);

      // Handle callback promise
      if (noCallback === false) {
        return new Promise((resolve, reject) => {
          let timeout = 5000;
          let elapsedTime = 200;

          if (key === 'changeSecurity') {
            timeout = appType === 'ios' ? 30000 : 15000;
          } else if (key === 'nativeBatchMutation' || key === 'signInNativeFirestore') {
            // Both are native round-trips that routinely exceed the 5s default and
            // fail closed on timeout (the `else` branch below rejects):
            //   • nativeBatchMutation's offline fallback acks 'ok:cached' at ~5s —
            //     a 5s poll is a dead heat that would give up right as the ack
            //     lands, skipping the single retry + native-error warning (onError).
            //   • signInNativeFirestore is Firebase Auth signInWithCustomToken; a
            //     premature reject surfaces FS-03 and needlessly blocks the student.
            // 15s keeps a comfortable margin past native's ~5s fallback
            // (PR #6578 / #6641 review).
            timeout = 15000;
          }

          newInterval[newPromiseId] = setInterval(() => {
            const returnedData = promisesData[newPromiseId];

            if (elapsedTime <= timeout) {
              if (returnedData) {
                clearInterval(newInterval[newPromiseId]);
                if (key === 'changeSecurity') {
                  setToLS('changeSecurityGuid', newPromiseId);
                  setToLS('changeSecurityValue', returnedData);
                }
                // Settle window — see IOS_SECURITY_ENABLE_SETTLE_MS.
                if (
                  isIos &&
                  key === 'changeSecurity' &&
                  value === true &&
                  returnedData === 'true'
                ) {
                  setTimeout(() => resolve(returnedData), IOS_SECURITY_ENABLE_SETTLE_MS);
                  return;
                }
                resolve(returnedData);
              } else if (returnedData === '') {
                clearInterval(newInterval[newPromiseId]);
                delete newInterval[newPromiseId];
                resolve(returnedData);
              }
            } else {
              clearInterval(newInterval[newPromiseId]);
              delete newInterval[newPromiseId];
              // Fail closed on timeout: callers (e.g. signInNativeFirestore,
              // nativeBatchMutation) rely on the rejection to surface a native
              // failure instead of hanging the awaiting promise forever.
              reject(new Error(`Timeout - ${key} - ${value}`));
            }

            elapsedTime += 200;
          }, 200);
        });
      }
    };
  }

  /**
   * APL-11641 — native-owned Firestore `user_response` writes.
   *
   * Builds the SAME Firestore document operations the web batch path builds
   * (`buildAndCommitCreateManyBatches` → `${resourcePath}${resource}/${id}`).
   * The user-response caller passes `resource: 'native_user_responses'` so the
   * native write lands at `experiences/{expId}/native_user_responses/{id}` — a
   * SEPARATE collection from the web `user_responses` write, by design — but
   * instead of committing
   * them through the web Firestore SDK it hands the plain
   * `{ path, data, actionType }[]` array to the Mac native bridge. Native
   * (already signed into Firebase Auth via `signInNativeFirestore`) writes them
   * directly and stamps `updatedAt` server-side — so we intentionally do NOT
   * inject a `serverTimestamp()` sentinel here (it cannot serialize across the
   * bridge), and there is no 500-op chunking (native owns batching).
   *
   * Caller-side fire-and-forget: this runs IN PARALLEL with the existing web
   * `batchMutation` (it does not replace it) and never drives the saved/failed
   * status — the web write owns that. Native acks the batch with 'ok'
   * (server-confirmed), 'ok:cached' (saved to the offline cache, auto-syncs on
   * reconnect) or 'error:<reason>' (write failed). 'ok:cached' is success, so
   * only an 'error:' ack is retried — exactly once; a still-failing retry is
   * captured to Sentry. The await runs on a floating promise so the caller
   * stays fire-and-forget. Callers MUST gate on
   * `checkNativeMethodSupported(getAppType(), 'nativeBatchMutation')`
   * (Mac >= 13.0.0 / iOS >= 6.0.0) — a no-op on web / seb / win and older builds.
   *
   * NOTE: builds Firestore-shaped paths (global region only) — not for the
   * China / Supabase provider.
   */
  const nativeBatchMutation = ({
    values,
    onError,
  }: {
    values: NativeBatchMutationValue[];
    meta?: any;
    // Optional, fire-and-forget. Invoked once when the native write ultimately
    // fails — after the single internal retry still acks 'error:', or when the
    // dispatch throws. Lets the caller (handleSetUserResponse) surface a
    // native-write warning to the indicator. Omitting it preserves the existing
    // void / Sentry-only behavior; the return type stays `void`.
    onError?: () => void;
  }): void => {
    const operations: NativeBatchOperation[] = [];
    try {
      for (const value of values || []) {
        if (!value?.data) continue;
        const fullResource = `${value.resourcePath || ''}${value.resource}`;
        const actionType = value.actionType ?? 'set';
        if (value.ids?.length) {
          for (const id of value.ids) {
            operations.push({ path: `${fullResource}/${id}`, data: value.data, actionType });
          }
        } else {
          operations.push({ path: fullResource, data: value.data, actionType });
        }
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: { context: 'offlineAppService.nativeBatchMutation', valueCount: values?.length },
      });
      return;
    }
    if (!operations.length) return;

    const payload = JSON.stringify(operations);
    // 'ok' / 'ok:cached' are both success; only an 'error:' ack is retried.
    const isErrorAck = (res: unknown): boolean =>
      typeof res === 'string' && res.startsWith('error');

    void (async () => {
      try {
        const first = await executeAppMethod({ key: 'nativeBatchMutation', value: payload });
        if (!isErrorAck(first)) return;
        // Single retry on a failed write.
        const retry = await executeAppMethod({ key: 'nativeBatchMutation', value: payload });
        if (isErrorAck(retry)) {
          Sentry.captureException(new Error('nativeBatchMutation failed after one retry'), {
            extra: {
              context: 'offlineAppService.nativeBatchMutation',
              firstAck: first,
              retryAck: retry,
              operationCount: operations.length,
            },
          });
          onError?.();
        }
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            context: 'offlineAppService.nativeBatchMutation:dispatch',
            operationCount: operations.length,
          },
        });
        onError?.();
      }
    })();
  };

  finalServiceObject.nativeBatchMutation = nativeBatchMutation;
  finalServiceObject.executeAppMethod = executeAppMethod;
  return finalServiceObject;
};

// APL-11850 boot fix (2026-07-08): initializing at module scope crashes when
// this file is reached mid-import-cycle from an entry order other than v1's
// (getAppType's binding is still in its temporal dead zone → "Cannot access
// 'wg' before initialization"). The v4 shell's boot graph (contexts/realTime →
// this file) hits exactly that. Defer to first PROPERTY ACCESS via a Proxy —
// the export shape is unchanged for every consumer in both shells, and first
// access always happens post-boot (event handlers / effects), never at
// module-init. No spread/iteration consumers exist (grep-verified).
let lockdownServices: { [key: string]: any } | undefined;
const resolveLockdownServices = () => (lockdownServices ??= initializeLockdownAppServices());

export const offlineAppService: { [key: string]: any } = new Proxy(
  {},
  {
    get: (_target, prop) => resolveLockdownServices()[prop as string],
    has: (_target, prop) => (prop as string) in resolveLockdownServices(),
    ownKeys: () => Reflect.ownKeys(resolveLockdownServices()),
    getOwnPropertyDescriptor: (_target, prop) =>
      Object.getOwnPropertyDescriptor(resolveLockdownServices(), prop),
  }
);

/**
 * Signs the Mac native app into Firebase Auth with the Firestore custom token
 * (APL-11641) so native can own the user_response writes. Native acks
 * `'true'`/`'false'` over the `executePromise` channel; the bridge rejects on timeout.
 *
 * Only does anything on the Mac app (>= 13.0.0) or iOS app (>= 6.0.0) — every other
 * build keeps the web batchMutation, so this returns `null` immediately there. On a
 * supported native build it
 * returns `null` when native acked success, or an `Error` tagged
 * `name: 'NativeFirestoreSignInError'` when it couldn't sign in — missing token, a
 * non-`'true'` ack, or a timeout / bridge error. The caller surfaces that Error as
 * the FS-03 page (matching on that name)
 * so a student never takes a test whose responses native can't save. Failures are
 * logged to Sentry here.
 */
export const signInNativeFirestore = async (
  token: string | null | undefined
): Promise<Error | null> => {
  // Only the Mac app (>= 13.0.0) / iOS app (>= 6.0.0) owns the native writes; every
  // other build keeps the web batchMutation, so there's nothing to sign in or block on.
  if (!checkNativeMethodSupported(getAppType(), 'signInNativeFirestore')) {
    return null;
  }

  const nativeError = Object.assign(
    new Error('Native Firestore sign-in (signInNativeFirestore) failed'),
    { name: 'NativeFirestoreSignInError' }
  );

  if (!token) {
    Sentry.captureException(nativeError);
    return nativeError;
  }

  try {
    const result = await offlineAppService.executeAppMethod({
      key: 'signInNativeFirestore',
      value: token,
    });
    if (result === 'true' || result === 'ok') return null;

    Sentry.captureException(
      new Error(
        `Native Firestore sign-in (signInNativeFirestore) returned failure: ${String(result)}`
      )
    );
    return nativeError;
  } catch (error) {
    // Timeout / bridge error on a build that's supposed to own the writes — fail
    // closed so the caller surfaces FS-03 rather than assuming native is saving.
    Sentry.captureException(error);
    return nativeError;
  }
};

/**
 * Asks native for its Firestore pending-writes status on the Student Experience
 * mount (APL-11641) and resolves as soon as native RESPONDS — both 'synced' and
 * 'pending' proceed. Native owns the writes and auto-syncs pending ones in the
 * background, so React only blocks until native answers, not until it finishes
 * flushing. Bounded by timeoutMs so an unresponsive native fails open ('timeout').
 * No-op ('synced') on non-native builds. Global Firestore only — not the China path.
 */
export const waitForNativePendingWritesSynced = async (): Promise<'synced' | 'pending'> => {
  // 1. Guard clause: If native method isn't supported, exit early
  if (!checkNativeMethodSupported(getAppType(), 'getFirestorePendingWritesStatus')) {
    return 'synced';
  }

  // 2. Direct call to native bridge (timeout is now handled by the caller wrapper)
  const result = await offlineAppService.executeAppMethod({
    key: 'getFirestorePendingWritesStatus',
  });

  // Dev/staging only — a debugging aid for native pending-writes status
  if (isDevelopment || isStaging) {
    message.info(`result ${result}`);
  }

  // 3. Return status cleanly
  return result === 'synced' ? 'synced' : 'pending';
};
